import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { FaArrowLeft, FaSignOutAlt, FaChevronDown, FaChevronRight, FaCheckCircle, FaBullhorn, FaHandshake, FaDraftingCompass, FaFileAlt, FaUserTie, FaUser, FaUserShield, FaCog, FaMapMarkerAlt, FaBuilding, FaPause, FaSync, FaComment, FaFolderOpen, FaEnvelope, FaPhone, FaClipboardList, FaFolder, FaTimes, FaExternalLinkAlt, FaHardHat, FaSearch, FaFileInvoiceDollar, FaClock } from 'react-icons/fa'
import { FaWhatsapp } from 'react-icons/fa6'
import ServiceHealthBar from '../../components/ServiceHealthBar'
import ProjectPicker, { QuickProjectSearch } from './ProjectPicker'
import EstimatesPicker from './EstimatesPicker'
import FlowCanvas from '../../components/FlowCanvas'
import { fetchAllProjects, fetchProjectsByEmail, fetchEstimatesBoard, fetchUserInfo, postMondayComment, fetchItemUpdates, fetchItemCreatedAt } from '../../lib/monday'
import { PROJECT_BOARD_IDS as _PROJECT_BOARD_IDS, ESTIMATES_BOARD_ID as _ESTIMATES_BOARD_ID, extractProject, resolveActiveStep, getStatusPhaseColor } from '../../lib/workflowMapping'
import { postClientComment, fetchConstructionProjects, fetchGHLContactNotes, postGHLContactNote } from '../../lib/ghl'
import { CON_PM_FLOW, CON_CLIENT_FLOW, CON_PHASES, resolveConstructionStep, getConstructionPhaseColor, getPhaseIndex, STAGE_NAMES, VENTAS_PIPELINE_ID, CONSTRUCCION_PIPELINE_ID } from '../../lib/constructionMapping'
import { buildConLinkIndex, resolveConDriveLink } from '../../lib/conDriveLinks'
import { useSessionState } from '../../hooks/useSessionState'
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh'
import { useRole } from '../../hooks/useRole'
import { useStageTimers, recordStageTransition, formatDuration, buildNodeTimerMap, fetchProjectStartDate, saveProjectStartDate } from '../../lib/workflowTimers'

// ── Construction Drive Link enrichment ────────────────────────────────────────
// Scans Monday construction board items for link columns and matches them to
// GHL projects by ADDRESS — never by client name/email alone (a client can
// have many projects). Ambiguous → no link (see lib/conDriveLinks.js).
const CON_MONDAY_BOARD = '18157237865'
const CON_EMAIL_COL    = 'text_mkt9qv82'
const CON_NAME_COL     = 'text_mkt9adtr'

// ── Phase palettes ───────────────────────────────────────────────────────────
// Estimates board: 2 phases (Marketing, Venta)
const ESTIMATE_PHASES = [
    { id: 'marketing',  key: 'admin.workflow.phase_marketing',  icon: FaBullhorn,        grad: 'from-rose-500 to-pink-500',    line: 'border-rose-300',  badge: 'bg-rose-500',  light: 'bg-rose-50 border-rose-200',   ring: 'ring-rose-300',  card: 'bg-rose-50 border-rose-300' },
    { id: 'venta',      key: 'admin.workflow.phase_venta',      icon: FaHandshake,       grad: 'from-blue-500 to-indigo-500',  line: 'border-blue-300',  badge: 'bg-blue-500',  light: 'bg-blue-50 border-blue-200',   ring: 'ring-blue-300',  card: 'bg-blue-50 border-blue-300' },
]

// Projects board: 2 phases (Preliminar, Documentos)
const PROJECT_PHASES = [
    { id: 'preliminar', key: 'admin.workflow.phase_preliminar', icon: FaDraftingCompass,  grad: 'from-amber-500 to-orange-500', line: 'border-amber-300', badge: 'bg-amber-500', light: 'bg-amber-50 border-amber-200', ring: 'ring-amber-300', card: 'bg-amber-50 border-amber-300' },
    { id: 'documentos', key: 'admin.workflow.phase_documentos', icon: FaFileAlt,          grad: 'from-emerald-500 to-teal-500', line: 'border-emerald-300', badge: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200', ring: 'ring-emerald-300', card: 'bg-emerald-50 border-emerald-300' },
]

// Legacy alias for components that default to PHASES
const PHASES = [...ESTIMATE_PHASES, ...PROJECT_PHASES]

const ACTOR_TAG = {
    pm:     { label: 'admin.workflow.actor_pm',     icon: FaUserTie,    css: 'bg-amber-100 text-amber-800' },
    client: { label: 'admin.workflow.actor_client',  icon: FaUser,       css: 'bg-sky-100 text-sky-800' },
    admin:  { label: 'admin.workflow.actor_admin',   icon: FaUserShield, css: 'bg-emerald-100 text-emerald-800' },
    system: { label: 'admin.workflow.actor_system',  icon: FaCog,        css: 'bg-gray-100 text-gray-600' },
}

const COLS = 5

// ── ESTIMATE PM flow (16 cards — Marketing + Venta phases) ───────────────────
const ESTIMATE_PM_FLOW = [
    // Phase 0 — Marketing (6)
    { id: 'E1',  label: 'admin.workflow.est_e1',  phase: 0, actor: 'system' },
    { id: 'E2',  label: 'admin.workflow.est_e2',  phase: 0, actor: 'pm' },
    { id: 'E3',  label: 'admin.workflow.est_e3',  phase: 0, actor: 'pm' },
    { id: 'E4',  label: 'admin.workflow.est_e4',  phase: 0, actor: 'pm' },
    { id: 'E5',  label: 'admin.workflow.est_e5',  phase: 0, actor: 'pm' },
    { id: 'E6',  label: 'admin.workflow.est_e6',  phase: 0, actor: 'pm' },
    // Phase 1 — Venta (10)
    { id: 'E7',  label: 'admin.workflow.est_e7',  phase: 1, actor: 'pm' },
    { id: 'E8',  label: 'admin.workflow.est_e8',  phase: 1, actor: 'pm' },
    { id: 'E9',  label: 'admin.workflow.est_e9',  phase: 1, actor: 'pm' },
    { id: 'E10', label: 'admin.workflow.est_e10', phase: 1, actor: 'pm' },
    { id: 'E11', label: 'admin.workflow.est_e11', phase: 1, actor: 'pm' },
    { id: 'E12', label: 'admin.workflow.est_e12', phase: 1, actor: 'pm' },
    { id: 'E13', label: 'admin.workflow.est_e13', phase: 1, actor: 'pm', decision: true,
      routes: [{ to: 'E10', color: 'amber', label: 'admin.workflow.route_modification' }, { to: 'E14', color: 'red', label: 'admin.workflow.route_discarded' }],
      detail: [{ t: 'admin.workflow.est_e13_d1', c: 'text-green-600' }, { t: 'admin.workflow.est_e13_d2', c: 'text-red-600' }] },
    { id: 'E14', label: 'admin.workflow.est_e14', phase: 1, actor: 'system',
      detail: [{ t: 'admin.workflow.est_e14_d1', c: 'text-red-600' }] },
    { id: 'E15', label: 'admin.workflow.est_e15', phase: 1, actor: 'admin',
      detail: [{ t: 'admin.workflow.est_e15_d1', c: 'text-green-600' }] },
    { id: 'E16', label: 'admin.workflow.est_e16', phase: 1, actor: 'admin',
      detail: [{ t: 'admin.workflow.est_e16_d1', c: 'text-green-600' }] },
]

// ── PROJECT PM flow (39 cards — Preliminar + Documentos phases) ──────────────
const PROJECT_PM_FLOW = [
    // Phase 0 — Preliminar (19)
    { id: 'P1',  label: 'admin.workflow.pr_p1',  phase: 0, actor: 'pm' },
    { id: 'P2',  label: 'admin.workflow.pr_p2',  phase: 0, actor: 'pm' },
    { id: 'P3',  label: 'admin.workflow.pr_p3',  phase: 0, actor: 'pm' },
    { id: 'P4',  label: 'admin.workflow.pr_p4',  phase: 0, actor: 'pm' },
    { id: 'P5',  label: 'admin.workflow.pr_p5',  phase: 0, actor: 'pm' },
    { id: 'P6',  label: 'admin.workflow.pr_p6',  phase: 0, actor: 'client', decision: true,
      routes: [{ to: 'P7', color: 'green', label: 'admin.workflow.route_approved' }, { to: 'P8', color: 'red', label: 'admin.workflow.route_revisions' }],
      detail: [{ t: 'admin.workflow.pr_p6_d1', c: 'text-green-600' }, { t: 'admin.workflow.pr_p6_d2', c: 'text-red-600' }] },
    { id: 'P7',  label: 'admin.workflow.pr_p7',  phase: 0, actor: 'pm' },
    { id: 'P8',  label: 'admin.workflow.pr_p8',  phase: 0, actor: 'pm' },
    { id: 'P9',  label: 'admin.workflow.pr_p9',  phase: 0, actor: 'pm' },
    { id: 'P10', label: 'admin.workflow.pr_p10', phase: 0, actor: 'client', decision: true,
      routes: [{ to: 'P11', color: 'green', label: 'admin.workflow.route_approved' }, { to: 'P12', color: 'red', label: 'admin.workflow.route_revisions' }],
      detail: [{ t: 'admin.workflow.pr_p10_d1', c: 'text-green-600' }, { t: 'admin.workflow.pr_p10_d2', c: 'text-red-600' }] },
    { id: 'P11', label: 'admin.workflow.pr_p11', phase: 0, actor: 'pm' },
    { id: 'P12', label: 'admin.workflow.pr_p12', phase: 0, actor: 'pm' },
    { id: 'P13', label: 'admin.workflow.pr_p13', phase: 0, actor: 'pm' },
    { id: 'P14', label: 'admin.workflow.pr_p14', phase: 0, actor: 'client', decision: true,
      routes: [{ to: 'P15', color: 'green', label: 'admin.workflow.route_approved' }, { to: 'P16', color: 'red', label: 'admin.workflow.route_revisions' }],
      detail: [{ t: 'admin.workflow.pr_p14_d1', c: 'text-green-600' }, { t: 'admin.workflow.pr_p14_d2', c: 'text-red-600' }] },
    { id: 'P15', label: 'admin.workflow.pr_p15', phase: 0, actor: 'pm' },
    { id: 'P16', label: 'admin.workflow.pr_p16', phase: 0, actor: 'pm' },
    { id: 'P17', label: 'admin.workflow.pr_p17', phase: 0, actor: 'pm' },
    { id: 'P18', label: 'admin.workflow.pr_p18', phase: 0, actor: 'admin',
      detail: [{ t: 'admin.workflow.pr_p18_d1', c: 'text-green-600' }] },
    { id: 'P19', label: 'admin.workflow.pr_p19', phase: 0, actor: 'pm',
      detail: [{ t: 'admin.workflow.pr_p19_d1' }] },
    // Phase 1 — Documentos de Construcción (20)
    { id: 'P20', label: 'admin.workflow.pr_p20', phase: 1, actor: 'pm',
      detail: [{ t: 'admin.workflow.pr_p20_d1' }] },
    { id: 'P21', label: 'admin.workflow.pr_p21', phase: 1, actor: 'pm' },
    { id: 'P22', label: 'admin.workflow.pr_p22', phase: 1, actor: 'pm' },
    { id: 'P23', label: 'admin.workflow.pr_p23', phase: 1, actor: 'admin' },
    { id: 'P24', label: 'admin.workflow.pr_p24', phase: 1, actor: 'pm',
      detail: [{ t: 'admin.workflow.pr_p24_d1' }] },
    { id: 'P25', label: 'admin.workflow.pr_p25', phase: 1, actor: 'client', decision: true,
      routes: [{ to: 'P26', color: 'green', label: 'admin.workflow.route_approved' }, { to: 'P26', color: 'red', label: 'admin.workflow.route_rejects' }],
      detail: [{ t: 'admin.workflow.pr_p25_d1', c: 'text-green-600' }, { t: 'admin.workflow.pr_p25_d2', c: 'text-red-600' }] },
    { id: 'P26', label: 'admin.workflow.pr_p26', phase: 1, actor: 'pm' },
    { id: 'P27', label: 'admin.workflow.pr_p27', phase: 1, actor: 'pm' },
    { id: 'P28', label: 'admin.workflow.pr_p28', phase: 1, actor: 'pm' },
    { id: 'P29', label: 'admin.workflow.pr_p29', phase: 1, actor: 'admin' },
    { id: 'P30', label: 'admin.workflow.pr_p30', phase: 1, actor: 'pm' },
    { id: 'P31', label: 'admin.workflow.pr_p31', phase: 1, actor: 'client', decision: true,
      routes: [{ to: 'P32', color: 'green', label: 'admin.workflow.route_approved' }, { to: 'P20', color: 'red', label: 'admin.workflow.route_rejects' }],
      detail: [{ t: 'admin.workflow.pr_p31_d1', c: 'text-green-600' }, { t: 'admin.workflow.pr_p31_d2', c: 'text-red-600' }] },
    { id: 'P32', label: 'admin.workflow.pr_p32', phase: 1, actor: 'admin',
      detail: [{ t: 'admin.workflow.pr_p32_d1', c: 'text-green-600' }] },
    { id: 'P33', label: 'admin.workflow.pr_p33', phase: 1, actor: 'client' },
    { id: 'P34', label: 'admin.workflow.pr_p34', phase: 1, actor: 'pm' },
    { id: 'P35', label: 'admin.workflow.pr_p35', phase: 1, actor: 'pm' },
    { id: 'P36', label: 'admin.workflow.pr_p36', phase: 1, actor: 'admin' },
    { id: 'P37', label: 'admin.workflow.pr_p37', phase: 1, actor: 'pm' },
    { id: 'P38', label: 'admin.workflow.pr_p38', phase: 1, actor: 'pm' },
    { id: 'P39', label: 'admin.workflow.pr_p39', phase: 1, actor: 'client',
      detail: [{ t: 'admin.workflow.pr_p39_d1', c: 'text-green-600' }] },
]

// ── PROJECT Client flow (simplified 11-step view) ────────────────────────────
const PROJECT_CLIENT_FLOW = [
    { id: 'c0',  label: 'admin.workflow.cl_c0',  phase: 0, actor: 'client', detail: [{ t: 'admin.workflow.cl_c0_d1' }] },
    { id: 'c1',  label: 'admin.workflow.cl_c1',  phase: 0, actor: 'pm',     detail: [{ t: 'admin.workflow.cl_c1_d1' }] },
    { id: 'c2',  label: 'admin.workflow.cl_c2',  phase: 0, actor: 'pm',     detail: [{ t: 'admin.workflow.cl_c2_d1' }] },
    { id: 'c3',  label: 'admin.workflow.cl_c3',  phase: 0, actor: 'client', decision: true,
      detail: [{ t: 'admin.workflow.cl_c3_d1', c: 'text-green-600' }, { t: 'admin.workflow.cl_c3_d2', c: 'text-red-600' }] },
    { id: 'c4',  label: 'admin.workflow.cl_c4',  phase: 0, actor: 'pm',     detail: [{ t: 'admin.workflow.cl_c4_d1' }] },
    { id: 'c5',  label: 'admin.workflow.cl_c5',  phase: 0, actor: 'admin',  detail: [{ t: 'admin.workflow.cl_c5_d1' }] },
    { id: 'c6',  label: 'admin.workflow.cl_c6',  phase: 1, actor: 'admin',  detail: [{ t: 'admin.workflow.cl_c6_d1' }] },
    { id: 'c7',  label: 'admin.workflow.cl_c7',  phase: 1, actor: 'pm',     detail: [{ t: 'admin.workflow.cl_c7_d1' }] },
    { id: 'c8',  label: 'admin.workflow.cl_c8',  phase: 1, actor: 'client', detail: [{ t: 'admin.workflow.cl_c8_d1' }] },
    { id: 'c9',  label: 'admin.workflow.cl_c9',  phase: 1, actor: 'admin',  detail: [{ t: 'admin.workflow.cl_c9_d1' }] },
    { id: 'c10', label: 'admin.workflow.cl_c10', phase: 1, actor: 'pm',     detail: [{ t: 'admin.workflow.cl_c10_d1' }] },
]

// Legacy aliases for any external references
const PM_FLOW = PROJECT_PM_FLOW
const CLIENT_FLOW = PROJECT_CLIENT_FLOW

// ── Helpers ────────────────────────────────────────────────────────────────────
function chunk(arr, n) {
    const r = []
    for (let i = 0; i < arr.length; i += n) r.push(arr.slice(i, i + n))
    return r
}

function groupByPhase(flow) {
    const groups = []
    let cur = null
    flow.forEach(node => {
        if (!cur || cur.phase !== node.phase) {
            cur = { phase: node.phase, steps: [] }
            groups.push(cur)
        }
        cur.steps.push(node)
    })
    return groups
}

function useIsMobile(bp = 768) {
    const [mobile, setMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false)
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${bp - 1}px)`)
        const handler = (e) => setMobile(e.matches)
        setMobile(mq.matches)
        mq.addEventListener('change', handler)
        return () => mq.removeEventListener('change', handler)
    }, [bp])
    return mobile
}

// ── Step status helper ──────────────────────────────────────────────────────
function stepStatus(idx, activeStep) {
    if (idx < activeStep) return 'done'
    if (idx === activeStep) return 'active'
    return 'future'
}

// ── Drive Files button (role-aware) ─────────────────────────────────────────
// target='client' → resolves Previos/Archivos Cliente via drive-proxy
// target='pm'     → opens root project folder directly (Monday link)
function DriveFilesButton({ project, target = 'client' }) {
    const { t } = useTranslation()
    const [resolving, setResolving] = useState(false)
    const [status, setStatus] = useState(null) // null | 'creating' | 'error'
    const hasLink = !!project?.driveLink

    const handleClick = async (e) => {
        e.stopPropagation()
        if (!hasLink || resolving) return

        // PM → open root folder directly, no proxy needed
        if (target === 'pm') {
            window.open(project.driveLink, '_blank')
            return
        }

        // Client → resolve Previos/Archivos Cliente via drive-proxy
        const match = project.driveLink.match(/\/folders\/([a-zA-Z0-9_-]+)/)
        if (!match) {
            window.open(project.driveLink, '_blank')
            return
        }

        setResolving(true)
        setStatus(null)
        try {
            const { supabase } = await import('../../lib/supabase')
            const body = { folderId: match[1], subpath: ['Previos', 'Archivos Cliente'] }
            const { data, error } = await supabase.functions.invoke('drive-proxy', { body })
            if (error) throw error

            if (data?.created) {
                // Folder was just created — brief feedback before redirect
                setStatus('creating')
                await new Promise(r => setTimeout(r, 1200))
            }
            window.open(data?.url || project.driveLink, '_blank')
        } catch (err) {
            console.error('[DriveFiles] Error:', err)
            setStatus('error')
            setTimeout(() => setStatus(null), 3000)
            window.open(project.driveLink, '_blank')
        } finally {
            setResolving(false)
            setTimeout(() => setStatus(null), 2000)
        }
    }

    if (hasLink) {
        return (
            <button
                onClick={handleClick}
                disabled={resolving}
                title={
                    status === 'creating' ? t('admin.workflow.creating_folder')
                    : status === 'error' ? t('admin.workflow.folder_error')
                    : t('admin.workflow.client_files')
                }
                className={`inline-flex items-center justify-center w-[22px] h-[22px] rounded-full transition-colors ${
                    status === 'creating'
                        ? 'text-amber-600 bg-amber-50 animate-pulse'
                        : status === 'error'
                        ? 'text-red-500 bg-red-50'
                        : resolving
                        ? 'text-gray-400 bg-gray-100 animate-pulse'
                        : 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
                }`}
            >
                <FaFolderOpen size={10} />
            </button>
        )
    }

    return (
        <span
            title={t('admin.workflow.client_files')}
            className="inline-flex items-center justify-center w-[22px] h-[22px] text-gray-400 bg-gray-100 rounded-full cursor-default opacity-40"
        >
            <FaFolderOpen size={10} />
        </span>
    )
}

// ── Comment box (client active step) ────────────────────────────────────────
function CommentBox({ project }) {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const [error, setError] = useState(null)
    const maxLen = 1000

    const handleSubmit = async () => {
        if (!text.trim() || sending) return
        setSending(true)
        setError(null)
        try {
            // Post to GHL (contact note) and Monday (item update) in parallel
            const ghlPromise = postClientComment(project.clientEmail, project.name, text, project.boardId)
            const mondayPromise = postMondayComment(project.id, `💬 Client comment:\n${text}`)
            await Promise.allSettled([ghlPromise, mondayPromise])
            setSent(true)
            setText('')
            setTimeout(() => { setSent(false); setIsOpen(false) }, 2500)
        } catch (err) {
            console.error('[Workflow] Comment failed:', err)
            setError(t('admin.workflow.comment_error'))
        } finally {
            setSending(false)
        }
    }

    return (
        <div className="relative w-full" onClick={(e) => e.stopPropagation()}>
            {/* Trigger buttons row */}
            <div className="flex items-center justify-center gap-1.5 mt-2">
                <button
                    onClick={() => setIsOpen(o => !o)}
                    className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full transition-colors ${
                        isOpen
                            ? 'text-blue-700 bg-blue-100'
                            : 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    }`}
                >
                    <FaComment size={9} /> {t('admin.workflow.comment_btn')}
                </button>
                <DriveFilesButton project={project} target="client" />
            </div>

            {/* Expandable textarea — shared content */}
            <AnimatePresence>
                {isOpen && (() => {
                    const panel = (
                        <div className="p-2.5 bg-white rounded-lg border border-blue-200 shadow-lg">
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value.slice(0, maxLen))}
                                placeholder={t('admin.workflow.comment_placeholder')}
                                maxLength={maxLen}
                                rows={3}
                                disabled={sending || sent}
                                className="w-full text-sm md:text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 md:px-2 md:py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-blue-300 disabled:opacity-50"
                            />
                            <div className="flex items-center justify-between mt-1.5">
                                <span className="text-[9px] text-gray-400">{text.length}/{maxLen}</span>
                                <div className="flex gap-1.5">
                                    {sent ? (
                                        <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
                                            <FaCheckCircle size={10} /> {t('admin.workflow.comment_sent')}
                                        </span>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => { setIsOpen(false); setText(''); setError(null) }}
                                                disabled={sending}
                                                className="text-[10px] text-gray-500 hover:text-gray-700 px-2 py-0.5 rounded transition-colors"
                                            >
                                                {t('common.cancel')}
                                            </button>
                                            <button
                                                onClick={handleSubmit}
                                                disabled={!text.trim() || sending}
                                                className="text-[10px] font-semibold text-white bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 px-3 py-0.5 rounded transition-colors"
                                            >
                                                {sending ? '...' : t('common.submit')}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
                        </div>
                    )
                    return (
                        <>
                            {/* Mobile: inline flow */}
                            <motion.div
                                key="mobile-panel"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.15 }}
                                className="overflow-hidden mt-1 md:hidden"
                            >
                                {panel}
                            </motion.div>
                            {/* Desktop: absolute viewport-centered */}
                            <div
                                className="hidden md:block absolute top-full left-1/2 w-[min(28rem,80vw)] mt-2"
                                style={{ zIndex: 9999, transform: 'translateX(-50%)' }}
                            >
                                <motion.div
                                    key="desktop-panel"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                >
                                    {panel}
                                </motion.div>
                            </div>
                        </>
                    )
                })()}
            </AnimatePresence>
        </div>
    )
}

// ── PM Comment Box (PM/admin active step) ─────────────────────────────────────
const PM_PREFIX = '📋 PM: '

function PMCommentBox({ project, session }) {
    const [updates, setUpdates] = useState([])
    const [loadingUpdates, setLoadingUpdates] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const [error, setError] = useState(null)
    const maxLen = 1000

    const fetchUpdates = () => {
        if (!project?.id) return
        setLoadingUpdates(true)
        fetchItemUpdates(project.id, 3, session)
            .then(data => setUpdates(data))
            .catch(() => {})
            .finally(() => setLoadingUpdates(false))
    }

    useEffect(() => { fetchUpdates() }, [project?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    const openModal = () => { setModalOpen(true); fetchUpdates() }

    const closeModal = () => { setModalOpen(false); setText(''); setError(null); setSent(false) }

    const handleSubmit = async () => {
        if (!text.trim() || sending) return
        setSending(true)
        setError(null)
        try {
            await postMondayComment(project.id, `${PM_PREFIX}${text}`, session)
            setSent(true)
            setText('')
            const fresh = await fetchItemUpdates(project.id, 3, session)
            setUpdates(fresh)
            setTimeout(() => { setSent(false); closeModal() }, 2500)
        } catch {
            setError('Error al enviar')
        } finally {
            setSending(false)
        }
    }

    const relTime = (iso) => {
        const diff = Date.now() - new Date(iso).getTime()
        const m = Math.floor(diff / 60000)
        if (m < 1) return 'ahora'
        if (m < 60) return `${m}m`
        const h = Math.floor(m / 60)
        if (h < 24) return `${h}h`
        return `${Math.floor(h / 24)}d`
    }

    const stripHtml = (html) => html?.replace(/<[^>]*>/g, '').trim() || ''

    const renderComment = (u) => {
        const body = stripHtml(u.body)
        const isPmNote = body.startsWith('📋 PM:')
        const displayText = body.replace(/^📋 PM: /, '').replace(/^💬 Client comment:\n/, '')
        return { isPmNote, displayText }
    }

    return (
        <div className="relative mt-2 w-full" onClick={e => e.stopPropagation()}>
            {/* Inline preview feed with hover tooltips */}
            <div className="mb-1.5">
                {loadingUpdates ? (
                    <p className="text-[10px] text-gray-400 text-center py-1">Cargando…</p>
                ) : updates.length === 0 ? (
                    <p
                        className="text-[10px] text-gray-400 text-center italic py-1 cursor-pointer hover:text-violet-500 transition-colors"
                        onClick={openModal}
                    >
                        Sin comentarios
                    </p>
                ) : (
                    <div className="space-y-1">
                        {updates.map(u => {
                            const { isPmNote, displayText } = renderComment(u)
                            return (
                                <div key={u.id} className="relative group">
                                    <div
                                        className="transition-opacity cursor-pointer hover:opacity-75"
                                        onClick={openModal}
                                    >
                                        <div className={`rounded-lg px-2 py-1.5 ${isPmNote ? 'bg-violet-50 border border-violet-100' : 'bg-blue-50 border border-blue-100'}`}>
                                            <div className="flex items-center justify-between gap-1 mb-0.5">
                                                <span className={`text-[10px] font-semibold truncate max-w-[120px] ${isPmNote ? 'text-violet-700' : 'text-blue-700'}`}>
                                                    {isPmNote ? '📋' : '💬'} {u.creator?.name}
                                                </span>
                                                <span className="text-[9px] text-gray-400 shrink-0">{relTime(u.created_at)}</span>
                                            </div>
                                            <p className="text-[11px] text-gray-700 leading-snug line-clamp-2">{displayText}</p>
                                        </div>
                                    </div>
                                    {/* Full-text tooltip */}
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-[11px] leading-snug rounded-lg px-3 py-2 shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-[9999]">
                                        <p className="font-semibold text-[10px] mb-1 opacity-70">{isPmNote ? '📋' : '💬'} {u.creator?.name} · {relTime(u.created_at)}</p>
                                        {displayText}
                                        <div className="absolute top-full left-1/2 border-4 border-transparent -translate-x-1/2 border-t-gray-900" />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {/* Action buttons row */}
            <div className="flex items-center justify-center gap-1.5">
                <button
                    onClick={openModal}
                    className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full transition-colors text-violet-600 bg-violet-50 hover:bg-violet-100"
                >
                    <FaClipboardList size={9} /> Nota interna
                </button>
                <DriveFilesButton project={project} target="pm" />
                <a
                    href={`https://jdrivero.monday.com/boards/${project?.boardId}/pulses/${project?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Ver en Monday"
                    className="inline-flex justify-center items-center w-6 h-6 text-gray-400 rounded-full transition-colors hover:text-violet-500 hover:bg-violet-50"
                    onClick={e => e.stopPropagation()}
                >
                    <FaExternalLinkAlt size={9} />
                </a>
            </div>

            {/* Modal — rendered via portal at document.body */}
            {createPortal(
                <AnimatePresence>
                    {modalOpen && (
                        <div
                            key="pm-modal-backdrop"
                            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
                            onClick={closeModal}
                        >
                            <motion.div
                                key="pm-modal"
                                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                                transition={{ duration: 0.15 }}
                                className="w-full max-w-md bg-white rounded-xl shadow-2xl"
                                onClick={e => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="flex justify-between items-center px-4 py-3 rounded-t-xl border-b border-gray-100">
                                    <div>
                                        <h3 className="text-sm font-bold text-violet-700 flex items-center gap-1.5">
                                            <FaClipboardList size={12} /> Notas internas
                                        </h3>
                                        <div className="group relative mt-0.5">
                                            <p className="text-[11px] text-gray-500 truncate max-w-[280px] cursor-default">{project?.name}</p>
                                            <div className="absolute bottom-full left-0 mb-1.5 w-max max-w-sm bg-gray-900 text-white text-[11px] leading-snug rounded-lg px-3 py-1.5 shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-[10000]">
                                                {project?.name}
                                                <div className="absolute left-4 top-full border-4 border-transparent border-t-gray-900" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 items-center">
                                        <a
                                            href={`https://jdrivero.monday.com/boards/${project?.boardId}/pulses/${project?.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Ver en Monday"
                                            className="p-1 text-gray-300 rounded transition-colors hover:text-violet-500"
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <FaExternalLinkAlt size={13} />
                                        </a>
                                        <button onClick={closeModal} className="p-1 text-gray-400 rounded transition-colors hover:text-gray-600">
                                            <FaTimes size={14} />
                                        </button>
                                    </div>
                                </div>

                                {/* Comments list */}
                                <div className="overflow-y-auto px-4 py-3 space-y-2 max-h-64">
                                    {loadingUpdates ? (
                                        <p className="py-4 text-xs text-center text-gray-400">Cargando…</p>
                                    ) : updates.length === 0 ? (
                                        <p className="py-4 text-xs italic text-center text-gray-400">Sin comentarios aún</p>
                                    ) : (
                                        updates.map(u => {
                                            const { isPmNote, displayText } = renderComment(u)
                                            return (
                                                <div key={u.id} className={`rounded-lg px-3 py-2.5 ${isPmNote ? 'bg-violet-50 border border-violet-100' : 'bg-blue-50 border border-blue-100'}`}>
                                                    <div className="flex gap-1 justify-between items-center mb-1">
                                                        <span className={`text-xs font-semibold ${isPmNote ? 'text-violet-700' : 'text-blue-700'}`}>
                                                            {isPmNote ? '📋' : '💬'} {u.creator?.name}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400 shrink-0">{relTime(u.created_at)}</span>
                                                    </div>
                                                    <p className="text-xs leading-relaxed text-gray-700 whitespace-pre-wrap">{displayText}</p>
                                                </div>
                                            )
                                        })
                                    )}
                                </div>

                                {/* Compose area */}
                                <div className="px-4 pt-3 pb-4 border-t border-gray-100">
                                    <textarea
                                        value={text}
                                        onChange={e => setText(e.target.value.slice(0, maxLen))}
                                        placeholder="Nueva nota interna (solo visible para PMs)…"
                                        maxLength={maxLen}
                                        rows={3}
                                        disabled={sending || sent}
                                        className="px-3 py-2 w-full text-sm text-gray-700 bg-gray-50 rounded-lg border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50"
                                        autoFocus
                                    />
                                    <div className="flex justify-between items-center mt-2">
                                        <span className="text-[10px] text-gray-400">{text.length}/{maxLen}</span>
                                        <div className="flex gap-2">
                                            {sent ? (
                                                <span className="flex gap-1 items-center text-xs font-semibold text-emerald-600">
                                                    <FaCheckCircle size={11} /> Enviado
                                                </span>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={closeModal}
                                                        disabled={sending}
                                                        className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
                                                    >
                                                        Cancelar
                                                    </button>
                                                    <button
                                                        onClick={handleSubmit}
                                                        disabled={!text.trim() || sending}
                                                        className="text-xs font-semibold text-white bg-violet-500 hover:bg-violet-600 disabled:bg-gray-300 px-4 py-1.5 rounded-lg transition-colors"
                                                    >
                                                        {sending ? '…' : 'Enviar'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    )
}

// ── Construction Comment Box — client (GHL contact note) ──────────────────────
function ConCommentBox({ project }) {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const [error, setError] = useState(null)
    const maxLen = 1000

    const handleSubmit = async () => {
        if (!text.trim() || sending) return
        setSending(true)
        setError(null)
        try {
            const body = `💬 Client [${project.name || project.clientName}]: ${text.trim()}`
            await postGHLContactNote(project.contactId, body)
            setSent(true)
            setText('')
            setTimeout(() => { setSent(false); setIsOpen(false) }, 2500)
        } catch (err) {
            console.error('[Workflow] Con comment failed:', err)
            setError(t('admin.workflow.comment_error'))
        } finally {
            setSending(false)
        }
    }

    return (
        <div className="relative w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-1.5 mt-2">
                <button
                    onClick={() => setIsOpen(o => !o)}
                    className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full transition-colors ${
                        isOpen ? 'text-emerald-700 bg-emerald-100' : 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
                    }`}
                >
                    <FaComment size={9} /> {t('admin.workflow.comment_btn')}
                </button>
                <DriveFilesButton project={project} target="client" />
            </div>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        key="con-client-panel"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden mt-1"
                    >
                        <div className="p-2.5 bg-white rounded-lg border border-emerald-200 shadow-lg">
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value.slice(0, maxLen))}
                                placeholder={t('admin.workflow.comment_placeholder')}
                                maxLength={maxLen}
                                rows={3}
                                disabled={sending || sent}
                                className="w-full text-sm md:text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 md:px-2 md:py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-300 disabled:opacity-50"
                            />
                            <div className="flex items-center justify-between mt-1.5">
                                <span className="text-[9px] text-gray-400">{text.length}/{maxLen}</span>
                                <div className="flex gap-1.5">
                                    {sent ? (
                                        <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
                                            <FaCheckCircle size={10} /> {t('admin.workflow.comment_sent')}
                                        </span>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => { setIsOpen(false); setText(''); setError(null) }}
                                                disabled={sending}
                                                className="text-[10px] text-gray-500 hover:text-gray-700 px-2 py-0.5 rounded transition-colors"
                                            >{t('common.cancel')}</button>
                                            <button
                                                onClick={handleSubmit}
                                                disabled={!text.trim() || sending}
                                                className="text-[10px] font-semibold text-white bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 px-3 py-0.5 rounded transition-colors"
                                            >{sending ? '...' : t('common.submit')}</button>
                                        </>
                                    )}
                                </div>
                            </div>
                            {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

// ── Construction PM Comment Box (GHL contact notes) ────────────────────────────
const CON_PM_PREFIX = '📋 PM: '

function ConPMCommentBox({ project }) {
    const [notes, setNotes] = useState([])
    const [loadingNotes, setLoadingNotes] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const [error, setError] = useState(null)
    const maxLen = 1000

    const fetchNotes = () => {
        if (!project?.contactId) return
        setLoadingNotes(true)
        fetchGHLContactNotes(project.contactId, 5)
            .then(data => setNotes(data))
            .catch(() => {})
            .finally(() => setLoadingNotes(false))
    }

    useEffect(() => { fetchNotes() }, [project?.contactId]) // eslint-disable-line react-hooks/exhaustive-deps

    const openModal = () => { setModalOpen(true); fetchNotes() }
    const closeModal = () => { setModalOpen(false); setText(''); setError(null); setSent(false) }

    const handleSubmit = async () => {
        if (!text.trim() || sending) return
        setSending(true)
        setError(null)
        try {
            await postGHLContactNote(project.contactId, `${CON_PM_PREFIX}${text}`)
            setSent(true)
            setText('')
            const fresh = await fetchGHLContactNotes(project.contactId, 5)
            setNotes(fresh)
            setTimeout(() => { setSent(false); closeModal() }, 2500)
        } catch {
            setError('Error al enviar')
        } finally {
            setSending(false)
        }
    }

    const relTime = (dateStr) => {
        if (!dateStr) return ''
        const diff = Date.now() - new Date(dateStr).getTime()
        const m = Math.floor(diff / 60000)
        if (m < 1) return 'ahora'
        if (m < 60) return `${m}m`
        const h = Math.floor(m / 60)
        if (h < 24) return `${h}h`
        return `${Math.floor(h / 24)}d`
    }

    const renderNote = (n) => {
        const body = (n.body || '').trim()
        const isPmNote = body.startsWith('📋 PM:')
        const isClientNote = body.startsWith('💬 Client')
        const displayText = body.replace(/^📋 PM: /, '').replace(/^💬 Client \[[^\]]+\]: /, '')
        return { isPmNote, isClientNote, displayText }
    }

    return (
        <div className="relative mt-2 w-full" onClick={e => e.stopPropagation()}>
            <div className="mb-1.5">
                {loadingNotes ? (
                    <p className="text-[10px] text-gray-400 text-center py-1">Cargando…</p>
                ) : notes.length === 0 ? (
                    <p
                        className="text-[10px] text-gray-400 text-center italic py-1 cursor-pointer hover:text-emerald-500 transition-colors"
                        onClick={openModal}
                    >Sin notas aún — clic para agregar</p>
                ) : (
                    <div className="flex flex-col gap-1">
                        {notes.slice(0, 3).map((n, i) => {
                            const { isPmNote, isClientNote, displayText } = renderNote(n)
                            return (
                                <div
                                    key={n.id || i}
                                    className={`group relative text-[10px] px-2 py-1 rounded-lg border cursor-pointer transition-colors ${
                                        isPmNote
                                            ? 'text-violet-800 bg-violet-50 border-violet-200 hover:bg-violet-100'
                                            : isClientNote
                                            ? 'text-blue-800 bg-blue-50 border-blue-200 hover:bg-blue-100'
                                            : 'text-gray-700 bg-gray-50 border-gray-200 hover:bg-gray-100'
                                    }`}
                                    onClick={openModal}
                                    title={displayText}
                                >
                                    <span className="mr-1 font-semibold">
                                        {isPmNote ? '📋' : isClientNote ? '💬' : '📝'}
                                    </span>
                                    <span className="truncate">{displayText.slice(0, 60)}{displayText.length > 60 ? '…' : ''}</span>
                                    {n.dateAdded && (
                                        <span className="absolute right-1.5 top-1 text-[9px] opacity-50">{relTime(n.dateAdded)}</span>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
            <div className="flex items-center justify-center gap-1.5">
                <button
                    onClick={openModal}
                    className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full transition-colors text-violet-600 bg-violet-50 hover:bg-violet-100"
                >
                    <FaClipboardList size={9} /> Nota interna
                </button>
                <DriveFilesButton project={project} target="pm" />
            </div>
            {modalOpen && createPortal(
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={closeModal}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="p-5 w-full max-w-md bg-white rounded-2xl shadow-2xl"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-bold text-gray-800">
                                Notas GHL — {project.clientName || project.name}
                            </h3>
                            <button onClick={closeModal} className="text-gray-400 transition-colors hover:text-gray-600">
                                <FaTimes size={14} />
                            </button>
                        </div>
                        <div className="flex flex-col gap-1.5 mb-3 max-h-48 overflow-y-auto">
                            {loadingNotes ? (
                                <p className="py-2 text-xs text-center text-gray-400">Cargando…</p>
                            ) : notes.length === 0 ? (
                                <p className="py-2 text-xs italic text-center text-gray-400">Sin notas aún.</p>
                            ) : notes.map((n, i) => {
                                const { isPmNote, isClientNote, displayText } = renderNote(n)
                                return (
                                    <div
                                        key={n.id || i}
                                        className={`text-xs px-3 py-2 rounded-xl border ${
                                            isPmNote
                                                ? 'text-violet-800 bg-violet-50 border-violet-200'
                                                : isClientNote
                                                ? 'text-blue-800 bg-blue-50 border-blue-200'
                                                : 'text-gray-700 bg-gray-50 border-gray-200'
                                        }`}
                                    >
                                        <span className="mr-1 font-semibold">
                                            {isPmNote ? '📋 PM' : isClientNote ? '💬 Cliente' : '📝'}:
                                        </span>
                                        {displayText}
                                        {n.dateAdded && (
                                            <span className="ml-2 text-[10px] opacity-50">{relTime(n.dateAdded)}</span>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                        <div className="pt-3 border-t">
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value.slice(0, maxLen))}
                                placeholder="Agregar nota al contacto en GHL…"
                                maxLength={maxLen}
                                rows={3}
                                disabled={sending || sent}
                                className="px-3 py-2 w-full text-sm text-gray-700 bg-gray-50 rounded-xl border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-50"
                            />
                            <div className="flex justify-between items-center mt-2">
                                <span className="text-[10px] text-gray-400">{text.length}/{maxLen}</span>
                                <div className="flex gap-2">
                                    {sent ? (
                                        <span className="flex gap-1 items-center text-xs font-semibold text-emerald-600">
                                            <FaCheckCircle size={11} /> Enviado
                                        </span>
                                    ) : (
                                        <>
                                            <button onClick={closeModal} disabled={sending} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg transition-colors">
                                                Cancelar
                                            </button>
                                            <button
                                                onClick={handleSubmit}
                                                disabled={!text.trim() || sending}
                                                className="text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 px-4 py-1.5 rounded-lg transition-colors"
                                            >{sending ? '…' : 'Enviar'}</button>
                                        </>
                                    )}
                                </div>
                            </div>
                            {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
                        </div>
                    </motion.div>
                </div>,
                document.body
            )}
        </div>
    )
}

// ── Map card (single node) ─────────────────────────────────────────────────────
function MapCard({ node, stepNum, isOpen, onToggle, status = 'done', onHoverDecision, isClient, project, session, phases: phasesProp, nodeTimerMap, projectStartDate }) {
    const { t } = useTranslation()
    const phasesArr = phasesProp || PHASES
    const p = phasesArr[node.phase] || phasesArr[0]
    const actor = ACTOR_TAG[node.actor] || ACTOR_TAG['pm']
    const hasDetail = !!node.detail?.length
    const PhaseIcon = typeof p.icon === 'string' ? (CON_PHASE_ICONS[p.icon] || FaFolder) : p.icon
    const ActorIcon = actor?.icon
    const isFuture = status === 'future'
    const isActive = status === 'active'
    const durLabels = {
        week: t('admin.workflow.duration_week'),
        day: t('admin.workflow.duration_day'),
        hour: t('admin.workflow.duration_hour'),
        minute: t('admin.workflow.duration_minute'),
        lessThan: t('admin.workflow.duration_less_than'),
    }
    const timerInfo = nodeTimerMap?.get(node.id)
    const timerLabel = timerInfo
        ? isActive
            ? formatDuration(timerInfo.totalMs, durLabels)
            : formatDuration(timerInfo.totalMs, durLabels)
        : ''

    return (
        <div
            data-node-id={node.id}
            onMouseEnter={() => node.decision && node.routes?.length && onHoverDecision?.(node.id)}
            onMouseLeave={() => node.decision && onHoverDecision?.(null)}
            className={`flex relative flex-col items-center ${isFuture ? 'opacity-40 grayscale' : ''}`}
            style={{ width: 'clamp(100px, 18vw, 160px)' }}
        >
            {/* Step badge */}
            <span className={`absolute -top-2.5 -left-1 z-10 w-6 h-6 rounded-full ${node.decision ? 'bg-violet-500' : node.parallel ? 'bg-indigo-500' : p.badge} text-white text-[10px] font-bold flex items-center justify-center shadow`}>
                {node.decision ? '🔀' : node.parallel ? '||' : (stepNum ?? '·')}
            </span>

            {/* Active pulse ring */}
            {isActive && <span className={`absolute -inset-1 z-0 rounded-2xl ring-2 ${p.ring} animate-pulse pointer-events-none`} />}

            {/* Card */}
            <button
                onClick={() => hasDetail && onToggle()}
                className={`
                    w-full rounded-xl border-2 shadow-sm transition-all duration-200
                    px-2 py-3 sm:px-3 sm:py-4 text-center relative z-[1]
                    ${p.card}
                    ${node.decision ? 'border-dashed' : ''}
                    ${isActive ? `ring-2 ${p.ring} shadow-lg scale-105` : ''}
                    ${isOpen ? `ring-2 ${p.ring} shadow-lg scale-105` : 'hover:shadow-md hover:scale-[1.02]'}
                    ${hasDetail ? 'cursor-pointer' : 'cursor-default'}
                `}
            >
                {PhaseIcon && <PhaseIcon className="mx-auto mb-1 text-gray-500 opacity-40" size={14} />}
                <p className="text-[11px] sm:text-xs font-bold text-gray-800 leading-snug">{t(node.label)}</p>
                {actor && (
                    <span className={`inline-flex items-center gap-1 mt-1.5 text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${actor.css}`}>
                        {ActorIcon && <ActorIcon size={9} />} {t(actor.label)}
                    </span>
                )}
                {timerLabel && (
                    <div className="relative group">
                        <span className={`inline-flex items-center gap-1 mt-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                            {timerLabel}
                            {timerInfo.visits.length > 1 && <span className="text-[8px] opacity-60">×{timerInfo.visits.length}</span>}
                        </span>
                        {/* Tooltip: stage time + total elapsed (positioned to the right to avoid card overlap) */}
                        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 w-max max-w-56 bg-gray-900 text-white text-[10px] leading-snug rounded-lg px-2.5 py-1.5 shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-[9999]">
                            <p className="font-semibold opacity-80 mb-0.5">{isActive ? t('admin.workflow.stage_time_ongoing') : t('admin.workflow.stage_time')}</p>
                            <p>{formatDuration(timerInfo.totalMs, durLabels)}</p>
                            {projectStartDate && (
                                <>
                                    <div className="my-1 border-t border-white/20" />
                                    <p className="font-semibold opacity-80">{t('admin.workflow.total_since_start')}</p>
                                    <p>{formatDuration(Date.now() - new Date(projectStartDate).getTime(), durLabels)}</p>
                                </>
                            )}
                            <div className="absolute top-1/2 right-full border-4 border-transparent -translate-y-1/2 border-r-gray-900" />
                        </div>
                    </div>
                )}
                {hasDetail && (
                    <FaChevronDown className={`mx-auto mt-1 text-[8px] text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </button>
            {isClient && isActive && project?.boardId && <CommentBox project={project} />}
            {!isClient && isActive && project?.boardId && <PMCommentBox project={project} session={session} />}
            {isClient && isActive && project?.contactId && <ConCommentBox project={project} />}
            {!isClient && isActive && project?.contactId && <ConPMCommentBox project={project} />}
        </div>
    )
}

// ── Detail popover (below the row) ─────────────────────────────────────────────
function RowDetail({ node, onClose, phases: phasesProp }) {
    const { t } = useTranslation()
    if (!node?.detail?.length) return null
    const phasesArr = phasesProp || PHASES
    const p = phasesArr[node.phase] || phasesArr[0]

    return (
        <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden w-full"
        >
            <div className={`mx-auto max-w-sm rounded-xl border ${p.light} p-3 shadow-md my-2`}>
                <p className="font-bold text-xs text-gray-800 mb-1.5">{t(node.label)}</p>
                <ul className="flex flex-col gap-1">
                    {node.detail.map((d, i) => (
                        <li key={i} className={`text-[11px] flex items-start gap-1.5 ${d.c || 'text-gray-600'}`}>
                            <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                            {t(d.t)}
                        </li>
                    ))}
                </ul>
            </div>
        </motion.div>
    )
}

// ── Mobile timeline card ─────────────────────────────────────────────────────
function MobileCard({ node, stepNum, isOpen, onToggle, isLast, phase, status = 'done', isClient, project, session, phases: phasesProp, nodeTimerMap }) {
    const { t } = useTranslation()
    const phasesArr = phasesProp || PHASES
    const p = phasesArr[phase] || phasesArr[0]
    const actor = ACTOR_TAG[node.actor] || ACTOR_TAG['pm']
    const hasDetail = !!node.detail?.length
    const ActorIcon = actor?.icon
    const isFuture = status === 'future'
    const isActive = status === 'active'
    const durLabels = {
        week: t('admin.workflow.duration_week'),
        day: t('admin.workflow.duration_day'),
        hour: t('admin.workflow.duration_hour'),
        minute: t('admin.workflow.duration_minute'),
        lessThan: t('admin.workflow.duration_less_than'),
    }
    const timerInfo = nodeTimerMap?.get(node.id)
    const timerLabel = timerInfo ? formatDuration(timerInfo.totalMs, durLabels) : ''

    return (
        <div className={`flex relative gap-3 transition-all duration-300 ${isFuture ? 'opacity-40 grayscale' : ''}`}>
            {/* Timeline rail */}
            <div className="flex flex-col items-center w-9 shrink-0">
                <div className={`
                    w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-md
                    ${node.decision ? 'bg-violet-500' : node.parallel ? 'bg-indigo-500' : p.badge}
                    ${isActive ? 'ring-2 ring-offset-2 ring-amber-400 animate-pulse' : ''}
                `}>
                    {node.decision ? '🔀' : node.parallel ? '||' : (stepNum ?? '·')}
                </div>
                {!isLast && <div className={`w-0.5 flex-1 min-h-[16px] ${p.badge} opacity-25`} />}
            </div>

            {/* Card */}
            <div
                className={`
                    flex-1 mb-2 rounded-xl border bg-white shadow-sm
                    transition-all duration-200
                    ${isActive ? `ring-2 ${p.ring} shadow-md` : ''}
                    ${isOpen ? `ring-2 ${p.ring} shadow-md` : 'hover:shadow-md'}
                    ${hasDetail ? 'cursor-pointer' : ''}
                `}
                onClick={() => hasDetail && onToggle()}
            >
                <div className="px-3 py-2.5 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold leading-snug text-gray-800">{t(node.label)}</p>
                        {timerLabel && (
                            <span className={`inline-flex items-center gap-1 mt-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                                {timerLabel}
                                {timerInfo.visits.length > 1 && <span className="text-[8px] opacity-60">×{timerInfo.visits.length}</span>}
                            </span>
                        )}
                    </div>
                    {actor && (
                        <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${actor.css}`}>
                            {ActorIcon && <ActorIcon size={8} />} {t(actor.label)}
                        </span>
                    )}
                    {hasDetail && (
                        <FaChevronDown className={`text-gray-400 text-xs shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    )}
                </div>

                <AnimatePresence initial={false}>
                    {isOpen && hasDetail && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                        >
                            <div className={`px-3 pb-3 pt-1 border-t ${p.light} rounded-b-xl`}>
                                <ul className="flex flex-col gap-1 mt-1.5">
                                    {node.detail.map((d, i) => (
                                        <li key={i} className={`text-xs flex items-start gap-1.5 ${d.c || 'text-gray-600'}`}>
                                            <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                                            {t(d.t)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
                {isClient && isActive && project?.boardId && (
                    <div className="px-3 pb-2">
                        <CommentBox project={project} />
                    </div>
                )}
                {!isClient && isActive && project?.boardId && (
                    <div className="px-3 pb-2">
                        <PMCommentBox project={project} session={session} />
                    </div>
                )}
                {isClient && isActive && project?.contactId && (
                    <div className="px-3 pb-2">
                        <ConCommentBox project={project} />
                    </div>
                )}
                {!isClient && isActive && project?.contactId && (
                    <div className="px-3 pb-2">
                        <ConPMCommentBox project={project} />
                    </div>
                )}
            </div>
        </div>
    )
}

// ── Mobile timeline (grouped by phase) ───────────────────────────────────────
function MobileTimeline({ flow, activeStep, isClient = false, project, session, phases: phasesProp, nodeTimerMap }) {
    const { t } = useTranslation()
    const [openId, setOpenId] = useState(null)
    const toggle = (id) => setOpenId(prev => prev === id ? null : id)
    const groups = groupByPhase(flow)
    const phasesArr = phasesProp || PHASES
    let globalStep = 0

    return (
        <div className="flex flex-col">
            {groups.map((g, gi) => {
                const p = phasesArr[g.phase] || phasesArr[0]
                const PhaseIcon = typeof p.icon === 'string' ? (CON_PHASE_ICONS[p.icon] || FaFolder) : p.icon
                const groupStartIdx = flow.indexOf(g.steps[0])
                const groupEndIdx = flow.indexOf(g.steps[g.steps.length - 1])
                const bannerFuture = groupStartIdx > activeStep
                return (
                    <div key={p.id + gi}>
                        {/* Phase banner */}
                        <div className={`flex items-center gap-2.5 mb-3 mt-1 transition-all duration-300 ${bannerFuture ? 'grayscale opacity-40' : ''}`}>
                            <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${p.grad} flex items-center justify-center text-white shadow-sm`}>
                                <PhaseIcon size={14} />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-sm font-bold text-gray-800">{t(p.key)}</h3>
                                <p className="text-[10px] text-gray-400">{t('admin.workflow.steps_count', { count: g.steps.length })}</p>
                            </div>
                            <div className={`h-px flex-1 ${p.badge} opacity-20`} />
                        </div>

                        {g.steps.map((node, si) => {
                            const idx = globalStep
                            globalStep++
                            return (
                                <MobileCard
                                    key={node.id}
                                    node={node}
                                    stepNum={isClient ? null : globalStep}
                                    phase={g.phase}
                                    isOpen={openId === node.id}
                                    onToggle={() => toggle(node.id)}
                                    isLast={si === g.steps.length - 1 && gi === groups.length - 1}
                                    status={stepStatus(idx, activeStep)}
                                    isClient={isClient}
                                    project={project}
                                    session={session}
                                    phases={phasesArr}
                                    nodeTimerMap={nodeTimerMap}
                                />
                            )
                        })}

                        {gi < groups.length - 1 && (
                            <div className="flex justify-center my-2">
                                <svg className="w-4 h-4 text-gray-300" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 7l6 6 6-6" /></svg>
                            </div>
                        )}
                    </div>
                )
            })}

            <div className="flex items-center gap-3 mt-3 pl-0.5">
                <div className="flex justify-center w-9">
                    <FaCheckCircle className="text-lg text-emerald-500" />
                </div>
                <p className="text-sm font-bold text-emerald-600">{t('admin.workflow.completed')}</p>
            </div>
        </div>
    )
}

// ── Serpentine map renderer ────────────────────────────────────────────────────
function FlowMap({ flow, cols, activeStep, isClient = false, project, session, phases: phasesProp, nodeTimerMap, projectStartDate }) {
    const { t } = useTranslation()
    const [openId, setOpenId] = useState(null)
    const [hoveredDecision, setHoveredDecision] = useState(null)
    const containerRef = useRef(null)
    const toggle = (id) => setOpenId(prev => prev === id ? null : id)
    const rows = chunk(flow, cols)

    return (
        <div ref={containerRef} className="flex relative flex-col items-center overflow-visible">
            {!isClient && <FlowCanvas flow={flow} hoveredDecision={hoveredDecision} containerRef={containerRef} activeStep={activeStep} t={t} />}
            {rows.map((row, ri) => {
                const reversed = ri % 2 === 1
                const ordered = reversed ? [...row].reverse() : row
                const baseIdx = ri * cols
                const rowLastIdx = baseIdx + row.length - 1

                return (
                    <div key={ri} className="py-3 w-full">
                        {/* Row of cards */}
                        <div className={`flex items-start justify-center gap-4 sm:gap-6 ${reversed ? 'flex-row-reverse' : ''}`}>
                            {ordered.map((node, ni) => {
                                const stepNum = reversed
                                    ? baseIdx + (row.length - 1 - ni) + 1
                                    : baseIdx + ni + 1
                                const idx = stepNum - 1
                                return (
                                    <MapCard
                                        key={node.id}
                                        node={node}
                                        stepNum={isClient ? null : stepNum}
                                        isOpen={openId === node.id}
                                        onToggle={() => toggle(node.id)}
                                        status={stepStatus(idx, activeStep)}
                                        onHoverDecision={setHoveredDecision}
                                        isClient={isClient}
                                        project={project}
                                        session={session}
                                        phases={phasesProp || PHASES}
                                        nodeTimerMap={nodeTimerMap}
                                        projectStartDate={projectStartDate}
                                    />
                                )
                            })}
                        </div>

                        {/* Detail popover */}
                        <AnimatePresence>
                            {row.some(n => n.id === openId) && (
                                <RowDetail
                                    node={flow.find(n => n.id === openId)}
                                    onClose={() => setOpenId(null)}
                                    phases={phasesProp || PHASES}
                                />
                            )}
                        </AnimatePresence>
                    </div>
                )
            })}

            {/* Completion */}
            <div className="flex gap-2 items-center mt-4">
                <FaCheckCircle className="text-lg text-emerald-500" />
                <span className="text-sm font-bold text-emerald-600">{t('admin.workflow.completed')}</span>
            </div>
        </div>
    )
}

// ── Construction project row (inside a client card) ──────────────────────────
function ConProjectRow({ project, onSelect }) {
    const phaseCss = getConstructionPhaseColor(project.stageId)
    const stageName = STAGE_NAMES[project.stageId] || ''
    const pipelineLabel = project.pipelineId === VENTAS_PIPELINE_ID ? 'Ventas' : 'Construcción'
    return (
        <button
            onClick={() => onSelect(project)}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors group"
        >
            <FaMapMarkerAlt className="text-gray-400 transition-colors shrink-0 group-hover:text-primary" size={12} />
            <div className="overflow-hidden flex-1 min-w-0">
                <p className="text-xs font-semibold leading-snug text-gray-800 truncate">{project.name}</p>
                <p className="text-[10px] text-gray-500 truncate mt-0.5">
                    {project.clientName && <><FaUser size={7} className="inline mr-0.5 -mt-px" />{project.clientName}</>}
                    {project.clientName && <span className="mx-1">·</span>}
                    <FaHardHat size={7} className="inline mr-0.5 -mt-px" />{pipelineLabel}
                </p>
            </div>
            {stageName && (
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${phaseCss}`}>
                    {stageName}
                </span>
            )}
        </button>
    )
}

// ── Con client card (desktop grid) ────────────────────────────────────────────
function ConClientCard({ displayName, projects, onSelect }) {
    const [expanded, setExpanded] = useState(false)
    const first = projects[0]
    const rest = projects.slice(1)
    const hasMore = rest.length > 0
    return (
        <div className="flex overflow-hidden flex-col bg-white rounded-2xl border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex gap-3 items-center px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="flex justify-center items-center w-9 h-9 text-sm font-bold text-white bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full shrink-0">
                    {(displayName || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{displayName || 'Sin nombre'}</p>
                    <p className="text-[11px] text-gray-500">{projects.length} proyecto{projects.length > 1 ? 's' : ''}</p>
                </div>
            </div>
            <div className="divide-y divide-gray-100">
                <ConProjectRow project={first} onSelect={onSelect} />
            </div>
            {hasMore && (
                <>
                    <AnimatePresence initial={false}>
                        {expanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden border-t border-gray-100 divide-y divide-gray-100"
                            >
                                {rest.map(p => (
                                    <ConProjectRow key={p.id} project={p} onSelect={onSelect} />
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <button
                        onClick={() => setExpanded(e => !e)}
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                    >
                        {expanded
                            ? <><FaChevronDown size={8} className="rotate-180" /> Ocultar</>
                            : <><FaChevronDown size={8} /> Ver {rest.length} más</>}
                    </button>
                </>
            )}
        </div>
    )
}

// ── Con client group (mobile accordion) ────────────────────────────────────────
function ConClientGroup({ displayName, projects, onSelect }) {
    const [expanded, setExpanded] = useState(projects.length <= 3)
    return (
        <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
            <button
                onClick={() => setExpanded(e => !e)}
                className="flex gap-3 items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
            >
                <div className="flex justify-center items-center w-8 h-8 text-xs font-bold text-white bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full shrink-0">
                    {(displayName || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{displayName || 'Sin nombre'}</p>
                    <p className="text-[11px] text-gray-500">{projects.length} proyecto{projects.length > 1 ? 's' : ''}</p>
                </div>
                {expanded ? <FaChevronDown size={10} className="text-gray-400" /> : <FaChevronRight size={10} className="text-gray-400" />}
            </button>
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden border-t border-gray-100 divide-y divide-gray-100"
                    >
                        {projects.map(p => (
                            <ConProjectRow key={p.id} project={p} onSelect={onSelect} />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

// ── Construction filter definitions ────────────────────────────────────────────
const CON_PIPELINE_FILTERS = [
    { id: VENTAS_PIPELINE_ID,       key: 'admin.workflow.con_pipeline_ventas',       active: 'bg-rose-500 text-white border-rose-500 shadow-sm',    inactive: 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100' },
    { id: CONSTRUCCION_PIPELINE_ID, key: 'admin.workflow.con_pipeline_construccion', active: 'bg-emerald-600 text-white border-emerald-600 shadow-sm', inactive: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
]

const CON_PHASE_FILTER_COLORS = [
    { active: 'bg-rose-500 text-white border-rose-500 shadow-md',    inactive: 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100' },
    { active: 'bg-orange-500 text-white border-orange-500 shadow-md', inactive: 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100' },
    { active: 'bg-indigo-500 text-white border-indigo-500 shadow-md', inactive: 'bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-100' },
    { active: 'bg-amber-500 text-white border-amber-500 shadow-md',   inactive: 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100' },
    { active: 'bg-emerald-500 text-white border-emerald-500 shadow-md', inactive: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
]

// ── Construction Picker ────────────────────────────────────────────────────────
function ConstructionPicker({ projects, loading, error, onSelect, refreshing }) {
    const { t } = useTranslation()
    const [search, setSearch] = useState('')
    const [pipelineFilter, setPipelineFilter] = useState(null)
    const [phaseFilter, setPhaseFilter] = useState(null)

    const handlePipelineFilter = (id) => {
        setPipelineFilter(id)
        setPhaseFilter(null)
    }

    // Projects scoped to pipeline filter — used for phase chip counts
    const pipelineFiltered = useMemo(() =>
        pipelineFilter ? projects.filter(p => p.pipelineId === pipelineFilter) : projects
    , [projects, pipelineFilter])

    const filtered = useMemo(() => {
        let result = pipelineFiltered
        if (search.trim()) {
            const q = search.toLowerCase()
            result = result.filter(p =>
                p.name.toLowerCase().includes(q) ||
                (p.clientName || '').toLowerCase().includes(q) ||
                (p.clientPhone || '').toLowerCase().includes(q)
            )
        }
        if (phaseFilter !== null) {
            result = result.filter(p => getPhaseIndex(p.stageId) === phaseFilter)
        }
        return result
    }, [pipelineFiltered, search, phaseFilter])

    // Group filtered projects by client (same logic as arch)
    const grouped = useMemo(() => {
        const map = new Map()
        filtered.forEach(p => {
            const email = p.clientEmail?.trim().toLowerCase()
            const key = email || p.clientName?.trim() || p.clientPhone?.trim() || '__none__'
            if (!map.has(key)) map.set(key, { projects: [], names: new Set(), phone: '' })
            const entry = map.get(key)
            entry.projects.push(p)
            if (p.clientName?.trim()) entry.names.add(p.clientName.trim())
            if (!entry.phone && p.clientPhone?.trim()) entry.phone = p.clientPhone.trim()
        })
        return Array.from(map.values())
            .map(({ projects: ps, names, phone }) => {
                const nameVariants = Array.from(names)
                const displayName = nameVariants.length > 0
                    ? nameVariants.reduce((a, b) => a.length >= b.length ? a : b)
                    : (phone || 'Sin nombre')
                return { displayName, projects: ps }
            })
            .filter(g => g.projects.length > 0)
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
    }, [filtered])

    if (loading) {
        return (
            <div className="mx-auto max-w-2xl lg:max-w-5xl">
                <div className="hidden grid-cols-2 gap-3 lg:grid xl:grid-cols-3">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="overflow-hidden bg-white rounded-2xl border border-gray-200 animate-pulse">
                            <div className="flex gap-3 items-center px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                                <div className="w-9 h-9 bg-gray-200 rounded-full" />
                                <div className="flex-1 space-y-1.5">
                                    <div className="h-3.5 bg-gray-200 rounded w-2/3" />
                                    <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                                </div>
                            </div>
                            <div className="px-4 py-3 space-y-2">
                                <div className="flex gap-2 items-center">
                                    <div className="w-3 h-3 bg-gray-200 rounded-full shrink-0" />
                                    <div className="flex-1 h-3 bg-gray-200 rounded" />
                                    <div className="w-14 h-4 bg-gray-200 rounded-full shrink-0" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col gap-2 lg:hidden">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
                    ))}
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="px-4 py-6 text-sm text-center text-red-500 bg-red-50 rounded-xl border border-red-200">
                {error}
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-2xl lg:max-w-5xl">
            {/* Search bar — identical to Arch */}
            <div className="relative mb-4">
                <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('admin.workflow.con_search_placeholder')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition shadow-sm"
                />
                {search && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
                        {filtered.length} de {pipelineFiltered.length}
                    </span>
                )}
            </div>

            {/* Pipeline filter chips — styled like Arch board chips */}
            {projects.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1 mb-2 scrollbar-none">
                    <button
                        onClick={() => handlePipelineFilter(null)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                            pipelineFilter === null
                                ? 'bg-gray-700 text-white border-gray-700 shadow-md'
                                : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                    >
                        {t('admin.workflow.filter_all')}
                        <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{projects.length}</span>
                    </button>
                    {CON_PIPELINE_FILTERS.map(f => {
                        const count = projects.filter(p => p.pipelineId === f.id).length
                        const isActive = pipelineFilter === f.id
                        return (
                            <button
                                key={f.id}
                                onClick={() => handlePipelineFilter(isActive ? null : f.id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                                    isActive ? f.active : f.inactive
                                }`}
                            >
                                {t(f.key)}
                                <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{count}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Phase filter chips — styled like Arch status chips */}
            {projects.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3 scrollbar-none">
                    <button
                        onClick={() => setPhaseFilter(null)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                            phaseFilter === null
                                ? 'bg-gray-700 text-white border-gray-700 shadow-md'
                                : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                    >
                        {t('admin.workflow.filter_all')}
                        <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{pipelineFiltered.length}</span>
                    </button>
                    {CON_PHASES.map((phase, idx) => {
                        const colors = CON_PHASE_FILTER_COLORS[idx]
                        const count = pipelineFiltered.filter(p => getPhaseIndex(p.stageId) === idx).length
                        const isActive = phaseFilter === idx
                        return (
                            <button
                                key={phase.id}
                                onClick={() => setPhaseFilter(isActive ? null : idx)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                                    isActive ? colors.active : colors.inactive
                                }`}
                            >
                                {t(phase.key)}
                                <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{count}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {filtered.length === 0 ? (
                <p className="py-8 text-sm text-center text-gray-400">
                    {projects.length === 0 ? t('admin.workflow.con_no_projects') : t('admin.workflow.con_no_results')}
                </p>
            ) : (
                <>
                    {/* Desktop: card grid */}
                    <div className="hidden grid-cols-2 gap-3 lg:grid xl:grid-cols-3">
                        {grouped.map(g => (
                            <ConClientCard key={g.displayName} displayName={g.displayName} projects={g.projects} onSelect={onSelect} />
                        ))}
                    </div>
                    {/* Mobile: stacked accordion */}
                    <div className="flex flex-col gap-2 lg:hidden">
                        {grouped.map(g => (
                            <ConClientGroup key={g.displayName} displayName={g.displayName} projects={g.projects} onSelect={onSelect} />
                        ))}
                    </div>
                </>
            )}
            {refreshing && (
                <p className="text-xs text-center text-gray-400 animate-pulse">{t('admin.workflow.refreshing')}</p>
            )}
        </div>
    )
}

// ── Phase icon map (construction phases store icon keys as strings) ──────────
const CON_PHASE_ICONS = {
    FaBullhorn,
    FaHandshake,
    FaDraftingCompass,
    FaFileAlt,
    FaHardHat,
    FaFileInvoiceDollar,
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function WorkflowMap({ session, onReturn, onLogout }) {
    const { t } = useTranslation()
    const { role, permissions } = useRole()
    const isClient = role === 'client'
    const canViewArch = !!permissions['admin.workflow.view_architecture']
    const canViewCon = !!permissions['admin.workflow.view_construction']
    const isPm = canViewArch || canViewCon
    const isMobile = useIsMobile()

    // ── Workflow type: 'arch' | 'construction' ──────────────────────────────
    const [workflowType, setWorkflowType] = useSessionState('jdw_wf_tab', 'arch')
    const isConstruction = workflowType === 'construction'

    // Lock workflow type for permission-specific users
    useEffect(() => {
        if (!canViewArch && canViewCon && !isConstruction) setWorkflowType('construction')
        else if (canViewArch && !canViewCon && isConstruction) setWorkflowType('arch')
    }, [canViewArch, canViewCon, isConstruction, setWorkflowType])

    // ── Picker mode (PM/admin only) ─────────────────────────────────────────
    const [pickerMode, setPickerMode] = useSessionState('jdw_wf_picker', 'projects')

    const isEstimateMode = !isConstruction && !isClient && pickerMode === 'estimates'
    const flow   = isConstruction ? (isClient ? CON_CLIENT_FLOW : CON_PM_FLOW)
        : isEstimateMode ? ESTIMATE_PM_FLOW
        : (isClient ? PROJECT_CLIENT_FLOW : PROJECT_PM_FLOW)
    const phases = isConstruction ? CON_PHASES
        : isEstimateMode ? ESTIMATE_PHASES
        : PROJECT_PHASES

    // ── Arch project state ───────────────────────────────────────────────────
    const [projects, setProjects] = useState([])
    const [loadingProjects, setLoadingProjects] = useState(true)
    const [refreshingProjects, setRefreshingProjects] = useState(false)
    const [fetchError, setFetchError] = useState(null)

    // ── Construction project state ───────────────────────────────────────────
    const [conProjects, setConProjects] = useState([])
    const [loadingConProjects, setLoadingConProjects] = useState(false)
    const [refreshingConProjects, setRefreshingConProjects] = useState(false)
    const [conFetchError, setConFetchError] = useState(null)
    const conLoadedRef = useRef(false)

    const [selectedProject, setSelectedProject] = useState(null)
    const [projectStartDate, setProjectStartDate] = useState(null)
    const [elapsedTick, setElapsedTick] = useState(0)
    const [syncToast, setSyncToast] = useState(false)
    const markRefreshedRef = useRef(null)

    // ── Estimates state (lifted so tab switches don’t re-fetch) ────────────────
    const [estimatesData, setEstimatesData]         = useState({ groups: [], items: [] })
    const [estimatesLoading, setEstimatesLoading]   = useState(false)
    const [estimatesError, setEstimatesError]       = useState(null)
    const [estimatesRefreshing, setEstimatesRefreshing] = useState(false)
    const estimatesLoadedRef = useRef(false)

    // Estimate items mapped to project shape for QuickProjectSearch
    const estimateProjects = useMemo(() =>
        estimatesData.items.map(i => extractProject(i, _ESTIMATES_BOARD_ID, 'Estimates 2026'))
    , [estimatesData.items])

    // Stable user-ID dep — prevents re-fetch on every Supabase token refresh
    const userId = session?.user?.id

    // Fetch projects from Monday
    const loadProjects = useCallback(async ({ force = false } = {}) => {
        if (force) setRefreshingProjects(true)
        setFetchError(null)
        try {
            let rawItems
            if (isClient && session?.user?.email) {
                rawItems = await fetchProjectsByEmail(_PROJECT_BOARD_IDS, session.user.email, session, { force })
            } else {
                rawItems = await fetchAllProjects(_PROJECT_BOARD_IDS, session, { force })
            }
            const mapped = rawItems.map(({ item, boardId, boardName }) => extractProject(item, boardId, boardName))

            // Enrich with PM email/phone for client view
            if (isClient) {
                const pmIds = [...new Set(mapped.map(p => p.pmUserId).filter(Boolean))]
                if (pmIds.length > 0) {
                    const userMap = await fetchUserInfo(pmIds, session)
                    for (const p of mapped) {
                        const info = userMap[p.pmUserId]
                        if (info) {
                            p.pmEmail = info.email || ''
                            p.pmPhone = info.phone || ''
                        }
                    }
                }
            }

            setProjects(mapped)
            if (isClient && mapped.length === 1) setSelectedProject(mapped[0])
            markRefreshedRef.current?.()
        } catch (err) {
            console.error('Error fetching projects:', err)
            setFetchError(err.message || 'Error de conexión')
        } finally {
            setLoadingProjects(false)
            setRefreshingProjects(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClient, userId])

    useEffect(() => { loadProjects() }, [loadProjects])

    // Fetch construction projects from GHL (lazy — only when construction tab first opened)
    const loadConProjects = useCallback(async ({ force = false, q = '' } = {}) => {
        if (force || !conLoadedRef.current) {
            if (!conLoadedRef.current) setLoadingConProjects(true)
            else setRefreshingConProjects(true)
        } else return
        conLoadedRef.current = true
        setConFetchError(null)
        try {
            const [data, mondayRaw] = await Promise.all([
                fetchConstructionProjects(session, { force, q }),
                fetchAllProjects([CON_MONDAY_BOARD], session, { force }).catch(() => []),
            ])
            const linkIndex = buildConLinkIndex(mondayRaw, {
                emailColId: CON_EMAIL_COL,
                nameColId:  CON_NAME_COL,
            })
            const enriched = data.map(p => ({
                ...p,
                driveLink: resolveConDriveLink(p, linkIndex),
            }))
            setConProjects(enriched)
            markRefreshedRef.current?.()
        } catch (err) {
            console.error('Error fetching construction projects:', err)
            setConFetchError(err.message || 'Error de conexión')
        } finally {
            setLoadingConProjects(false)
            setRefreshingConProjects(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId])

    useEffect(() => {
        if (isConstruction && !conLoadedRef.current) loadConProjects()
    }, [isConstruction, loadConProjects])

    // Fetch estimates from Monday (lazy — only when Estimates tab first opened)
    const loadEstimates = useCallback(async ({ force = false } = {}) => {
        if (estimatesLoadedRef.current && !force) return
        if (!estimatesLoadedRef.current) {
            setEstimatesLoading(true)
        } else {
            setEstimatesRefreshing(true)
        }
        estimatesLoadedRef.current = true
        setEstimatesError(null)
        try {
            const result = await fetchEstimatesBoard(session, { force })
            setEstimatesData(result)
            markRefreshedRef.current?.()
        } catch (err) {
            setEstimatesError(err.message || 'Error')
        } finally {
            setEstimatesLoading(false)
            setEstimatesRefreshing(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId])

    // Trigger estimates load when tab is first switched to Estimates
    useEffect(() => {
        if (isPm && pickerMode === 'estimates') loadEstimates()
    }, [isPm, pickerMode, loadEstimates])

    // Silent background refresh on tab visibility restore (stale > 5 min)
    const { markRefreshed } = useVisibilityRefresh({
        staleMs: 300_000,
        onRefresh: useCallback(() => {
            setSyncToast(true)
            setTimeout(() => setSyncToast(false), 2000)
            if (workflowType === 'construction') {
                conLoadedRef.current = false
                loadConProjects({ force: true })
            } else if (pickerMode === 'estimates') {
                loadEstimates({ force: true })
            } else {
                loadProjects({ force: true })
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [workflowType, pickerMode]),
    })
    markRefreshedRef.current = markRefreshed

    // Persist selected project ID + type to sessionStorage
    useEffect(() => {
        try {
            if (selectedProject) {
                sessionStorage.setItem('jdw_wf_project', JSON.stringify({
                    id: selectedProject.id,
                    type: workflowType,
                    picker: pickerMode,
                }))
            } else {
                sessionStorage.removeItem('jdw_wf_project')
            }
        } catch {}
    }, [selectedProject, workflowType, pickerMode])

    // Restore last selected project after data loads (silent auto-select)
    useEffect(() => {
        if (selectedProject) return
        try {
            const stored = JSON.parse(sessionStorage.getItem('jdw_wf_project') || 'null')
            if (!stored) return
            if (stored.type === 'arch') {
                if (stored.picker === 'estimates' && estimatesData.items.length > 0) {
                    const raw = estimatesData.items.find(i => i.id === stored.id)
                    if (raw) {
                        setPickerMode('estimates')
                        setSelectedProject(extractProject(raw, _ESTIMATES_BOARD_ID, 'Estimates 2026'))
                    }
                } else if (projects.length > 0) {
                    const found = projects.find(p => p.id === stored.id)
                    if (found) { setWorkflowType('arch'); setSelectedProject(found) }
                }
            } else if (stored.type === 'construction' && conProjects.length > 0) {
                const found = conProjects.find(p => p.id === stored.id)
                if (found) { setWorkflowType('construction'); setSelectedProject(found) }
            }
        } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, conProjects, estimatesData])

    // Resolve active step from selected project
    const activeStep = selectedProject
        ? isConstruction
            ? resolveConstructionStep(selectedProject.stageId, !isClient)
            : resolveActiveStep(selectedProject, !isClient)
        : 0
    const isSuspended = activeStep === -1

    // ── Stage timers ─────────────────────────────────────────────────────────
    const { timers, refresh: refreshTimers } = useStageTimers(selectedProject?.id, workflowType)
    const nodeTimerMap = useMemo(
        () => buildNodeTimerMap(timers, flow, workflowType, !isClient, selectedProject?.boardId),
        [timers, flow, workflowType, isClient, selectedProject]
    )

    // Record stage transition when selected project changes (client-side fallback)
    useEffect(() => {
        if (!selectedProject || isSuspended) return
        const rawStageId = isConstruction
            ? selectedProject.stageId
            : String(selectedProject.statusWfId ?? '')
        const stageLabel = isConstruction
            ? (STAGE_NAMES[selectedProject.stageId] || selectedProject.stageId)
            : (selectedProject.statusWfText?.startsWith('admin.workflow.') ? t(selectedProject.statusWfText) : (selectedProject.statusWfText || ''))
        recordStageTransition(
            selectedProject.id,
            workflowType,
            isConstruction ? null : selectedProject.boardId,
            rawStageId,
            stageLabel
        ).then(didChange => {
            if (didChange) refreshTimers()
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject?.id, selectedProject?.stageId, selectedProject?.statusWfId, workflowType, isSuspended])

    // ── Fetch / cache project start date (Monday created_at or GHL createdAt) ─
    useEffect(() => {
        if (!selectedProject) { setProjectStartDate(null); return }
        let cancelled = false
        const run = async () => {
            const cached = await fetchProjectStartDate(selectedProject.id, workflowType)
            if (cancelled) return
            if (cached) {
                setProjectStartDate(cached)
                return
            }
            // Construction projects come from GHL — use GHL createdAt directly
            if (isConstruction && selectedProject.createdAt) {
                const ghlCreated = new Date(selectedProject.createdAt).toISOString()
                await saveProjectStartDate(
                    selectedProject.id,
                    workflowType,
                    null,
                    ghlCreated
                )
                setProjectStartDate(ghlCreated)
                return
            }
            // Architecture projects — fetch from Monday
            const mondayCreated = await fetchItemCreatedAt(selectedProject.id, session)
            if (cancelled) return
            if (mondayCreated) {
                await saveProjectStartDate(
                    selectedProject.id,
                    workflowType,
                    selectedProject.boardId,
                    mondayCreated
                )
                setProjectStartDate(mondayCreated)
            }
        }
        run()
        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject?.id, workflowType])

    // ── Live tick for project elapsed time ───────────────────────────────────
    useEffect(() => {
        if (!selectedProject || !projectStartDate) return
        const interval = setInterval(() => {
            setElapsedTick(t => t + 1)
        }, 60_000)
        return () => clearInterval(interval)
    }, [selectedProject?.id, projectStartDate])

    // ── Picker view (no project selected) ────────────────────────────────────
    if (!selectedProject) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="min-h-screen overflow-x-clip bg-cloud"
            >
                <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                    <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                        <button onClick={onReturn} className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0" title={t('common.return')}>
                            <FaArrowLeft />
                        </button>
                        <h1 className="flex-1 text-xl font-bold text-primary">{t('admin.workflow.title')}</h1>
                {/* Workflow type selector — only when user has BOTH permissions */}
                {canViewArch && canViewCon && (
                    <div className="inline-flex rounded-lg bg-gray-100 p-0.5 gap-0.5 shadow-inner shrink-0">
                        <button
                            onClick={() => { setWorkflowType('arch'); setSelectedProject(null) }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                !isConstruction ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title={t('admin.workflow.tab_arch')}
                        >
                            <FaDraftingCompass size={11} />
                            <span className="hidden sm:inline">{t('admin.workflow.tab_arch')}</span>
                        </button>
                        <button
                            onClick={() => { if (!conLoadedRef.current) setLoadingConProjects(true); setWorkflowType('construction'); setSelectedProject(null) }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                isConstruction ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title={t('admin.workflow.tab_construction')}
                        >
                            <FaHardHat size={11} />
                            <span className="hidden sm:inline">{t('admin.workflow.tab_construction')}</span>
                        </button>
                    </div>
                )}
                        <button onClick={onLogout} className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-red-600 hover:text-white hover:border-red-600 text-ash shrink-0" title={t('auth.signOut')}>
                            <FaSignOutAlt />
                        </button>
                    </div>
                    <div className="px-4 pb-3 mx-auto max-w-7xl sm:px-6">
                        <ServiceHealthBar />
                    </div>
                </header>

                <main className="px-3 py-6 mx-auto max-w-6xl sm:px-6">
                    <p className="mb-4 text-sm font-medium text-center text-gray-500">
                        {isClient ? t('admin.workflow.picker_client') : t('admin.workflow.picker_pm')}
                    </p>

                    {/* Refresh — construction only (mirrors arch mode-switcher row position) */}
                    {isConstruction && (
                        <div className="flex gap-2 justify-center items-center mb-5">
                            <button
                                onClick={() => { conLoadedRef.current = false; loadConProjects({ force: true }) }}
                                disabled={refreshingConProjects || loadingConProjects}
                                className="p-2 text-gray-400 rounded-lg border border-gray-200 transition-all hover:text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                title="Refresh"
                            >
                                <FaSync size={13} className={(refreshingConProjects || loadingConProjects) ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    )}

                    {/* Mode switcher + refresh — PM/admin only */}
                    {isPm && !isConstruction && (
                        <div className="flex gap-2 justify-center items-center mb-5">
                            <div className="inline-flex gap-1 p-1 bg-gray-100 rounded-xl shadow-inner">
                                <button
                                    onClick={() => setPickerMode('projects')}
                                    className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                                        pickerMode === 'projects'
                                            ? 'bg-white text-gray-800 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <FaFolder size={13} />
                                    {t('admin.workflow.tab_projects')}
                                </button>
                                <button
                                    onClick={() => setPickerMode('estimates')}
                                    className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                                        pickerMode === 'estimates'
                                            ? 'bg-white text-gray-800 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <FaClipboardList size={13} />
                                    {t('admin.workflow.tab_estimates')}
                                </button>
                            </div>
                            {/* Refresh button */}
                            <button
                                onClick={() => pickerMode === 'projects'
                                    ? loadProjects({ force: true })
                                    : loadEstimates({ force: true })
                                }
                                disabled={refreshingProjects || estimatesRefreshing}
                                className="p-2 text-gray-400 rounded-lg border border-gray-200 transition-all hover:text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                title="Refresh"
                            >
                                <FaSync
                                    size={13}
                                    className={(
                                        pickerMode === 'projects' ? refreshingProjects : estimatesRefreshing
                                    ) ? 'animate-spin' : ''}
                                />
                            </button>
                        </div>
                    )}

                    {/* Construction picker */}
                    {isConstruction ? (
                        <ConstructionPicker
                            projects={conProjects}
                            loading={loadingConProjects}
                            error={conFetchError}
                            onSelect={setSelectedProject}
                            refreshing={refreshingConProjects}
                        />
                    ) : isPm && pickerMode === 'estimates' ? (
                        <EstimatesPicker
                            data={estimatesData}
                            loading={estimatesLoading}
                            error={estimatesError}
                            refreshing={estimatesRefreshing}
                            onSelect={(item) => {
                                const extracted = extractProject(item, _ESTIMATES_BOARD_ID, 'Estimates 2026')
                                setSelectedProject(extracted)
                            }}
                        />
                    ) : (
                        <ProjectPicker
                            projects={projects}
                            loading={loadingProjects}
                            error={fetchError}
                            onSelect={setSelectedProject}
                            isPm={isPm}
                            refreshing={refreshingProjects}
                        />
                    )}
                </main>
            {syncToast && (
                <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 bg-gray-800/90 text-white text-xs font-medium rounded-full shadow-lg pointer-events-none">
                    {t('admin.workflow.syncing')}
                </div>
            )}
            </motion.div>
        )
    }

    // ── Map view (project selected) ──────────────────────────────────────────
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen overflow-x-clip bg-cloud"
        >
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                    <button onClick={() => setSelectedProject(null)} className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0" title={t('common.return')}>
                        <FaArrowLeft />
                    </button>
                    {/* Quick-switch search */}
                    <div className="flex-1 min-w-0">
                        <QuickProjectSearch
                            projects={isEstimateMode ? estimateProjects : (isConstruction ? conProjects : projects)}
                            currentProject={selectedProject}
                            onSwitch={setSelectedProject}
                        />
                    </div>
                    <button onClick={onLogout} className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-red-600 hover:text-white hover:border-red-600 text-ash shrink-0" title={t('auth.signOut')}>
                        <FaSignOutAlt />
                    </button>
                </div>

                {/* Project info bar */}
                <div className="px-4 pb-3 mx-auto max-w-7xl sm:px-6">
                    <div className="flex flex-wrap gap-y-1 gap-x-4 items-center mb-2 text-xs text-gray-600">
                        <span className="flex gap-1 items-center font-semibold text-gray-800">
                            <FaMapMarkerAlt size={10} className="text-primary" /> {selectedProject.name}
                        </span>
                        {selectedProject.clientName && (
                            <span className="flex gap-1 items-center">
                                <FaUser size={9} className="text-gray-400" /> {selectedProject.clientName}
                            </span>
                        )}
                        {selectedProject.office && (
                            <span className="flex gap-1 items-center">
                                <FaBuilding size={9} className="text-gray-400" /> {selectedProject.office}
                            </span>
                        )}
                        {isConstruction ? (
                            selectedProject.stageId && (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getConstructionPhaseColor(selectedProject.stageId)}`}>
                                    {STAGE_NAMES[selectedProject.stageId] || selectedProject.stageId}
                                </span>
                            )
                        ) : (
                            selectedProject.statusWfText && (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusPhaseColor(selectedProject)}`}>
                                    {selectedProject.statusWfText?.startsWith('admin.workflow.') ? t(selectedProject.statusWfText) : selectedProject.statusWfText}
                                </span>
                            )
                        )}
                        {projectStartDate && (
                            <span className="flex gap-1 items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700" title={t('admin.workflow.total_time_since_project_started')}>
                                <FaClock size={9} />
                                {formatDuration(Date.now() - new Date(projectStartDate).getTime(), {
                                    week: t('admin.workflow.duration_week'),
                                    day: t('admin.workflow.duration_day'),
                                    hour: t('admin.workflow.duration_hour'),
                                    minute: t('admin.workflow.duration_minute'),
                                    lessThan: t('admin.workflow.duration_less_than'),
                                })}
                            </span>
                        )}
                    </div>
                    {isClient && selectedProject.pmName && (
                        <div className="flex flex-wrap gap-y-1 gap-x-4 items-center mb-2 text-xs text-gray-600">
                            <span className="flex gap-1 items-center font-semibold text-amber-700">
                                <FaUserTie size={10} className="text-amber-500" /> {selectedProject.pmName}
                            </span>
                            {selectedProject.pmEmail && (
                                <a href={`mailto:${selectedProject.pmEmail}`} className="flex gap-1 items-center text-blue-600 hover:underline">
                                    <FaEnvelope size={9} /> {selectedProject.pmEmail}
                                </a>
                            )}
                            <a href="tel:+19729070414" className="flex gap-1 items-center text-gray-600 hover:underline">
                                <FaPhone size={9} /> (972) 907-0414
                            </a>
                            <a
                                href="https://wa.me/12144620683"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex gap-1 items-center font-medium text-green-600 hover:underline"
                            >
                                <FaWhatsapp size={11} /> WhatsApp
                            </a>
                        </div>
                    )}
                    <ServiceHealthBar />
                </div>
            </header>

            {/* Suspended overlay */}
            {isSuspended && (
                <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
                    <div className="flex gap-2 justify-center items-center text-gray-500">
                        <FaPause size={12} />
                        <span className="text-sm font-semibold">{t('admin.workflow.suspended')}</span>
                    </div>
                </div>
            )}

            <main className={`px-3 py-6 mx-auto max-w-6xl sm:px-6 ${isSuspended ? 'opacity-40 grayscale pointer-events-none' : ''}`}>
                {/* Legend */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4 justify-center text-[11px] text-gray-500">
                    {phases.map(p => {
                        const Icon = isConstruction ? (CON_PHASE_ICONS[p.icon] || FaFolder) : p.icon
                        return (
                            <span key={p.id} className="flex gap-1 items-center">
                                <span className={`w-5 h-5 rounded-full ${p.badge} flex items-center justify-center`}><Icon size={10} className="text-white" /></span> {t(p.key)}
                            </span>
                        )
                    })}
                    {Object.entries(ACTOR_TAG).map(([k, v]) => {
                        const Icon = v.icon
                        return (
                            <span key={k} className="flex gap-1 items-center">
                                <Icon size={10} className={v.css.split(' ')[1]} /> {t(v.label)}
                            </span>
                        )
                    })}
                    <span className="flex gap-1 items-center">
                        <span className="text-sm">🔀</span> {t('admin.workflow.decision')}
                    </span>
                </div>

                <p className="mb-6 text-xs text-center text-gray-400">
                    {isClient
                        ? t('admin.workflow.desc_client')
                        : t('admin.workflow.desc_pm')}
                </p>

                {isMobile ? (
                    <MobileTimeline flow={flow} activeStep={isSuspended ? -1 : activeStep} isClient={isClient} project={selectedProject} session={session} phases={phases} nodeTimerMap={nodeTimerMap} />
                ) : (
                    <FlowMap
                        flow={flow}
                        cols={isClient ? 5 : COLS}
                        activeStep={isSuspended ? -1 : activeStep}
                        isClient={isClient}
                        project={selectedProject}
                        session={session}
                        phases={phases}
                        nodeTimerMap={nodeTimerMap}
                        projectStartDate={projectStartDate}
                    />
                )}
            </main>
            {syncToast && (
                <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 bg-gray-800/90 text-white text-xs font-medium rounded-full shadow-lg pointer-events-none">
                    {t('admin.workflow.syncing')}
                </div>
            )}
        </motion.div>
    )
}
